/**
 * Server timestamps come without a timezone ("2026-09-25T13:57:31") and are US
 * market time (the backend runs on America/New_York). new Date() on such a string
 * treats it as the VIEWER's local time, which is wrong for anyone outside ET — so
 * always parse server times through serverTime().
 */

const NY = 'America/New_York';

/** Offset of New York from UTC (minutes) at a given instant, e.g. -240 in summer. */
function nyOffsetMinutes(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NY, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return Math.round((asUtc - date.getTime()) / 60000);
}

/** Server time string -> real Date (instant). Strings that already carry a zone are used as-is. */
export function serverTime(s) {
  if (!s) return null;
  const str = String(s);
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(str)) return new Date(str);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return new Date(str);
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  const off = nyOffsetMinutes(new Date(guess));
  return new Date(guess - off * 60000);
}

/** "Sep 25, 1:57 PM ET" — signal generation / publish / close times. */
export function fmtET(s, withDate = true) {
  const d = serverTime(s);
  if (!d || Number.isNaN(d.getTime())) return '';
  const opts = withDate
    ? { timeZone: NY, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { timeZone: NY, hour: 'numeric', minute: '2-digit' };
  return `${d.toLocaleString('en-US', opts)} ET`;
}

/** "Sep 25, 2026, 10:57 AM" in the VIEWER's own timezone — notifications. */
export function fmtLocal(s) {
  const d = serverTime(s);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "5m ago" etc., computed from the correct instant. */
export function timeAgo(s) {
  const d = serverTime(s);
  if (!d) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}
